import { GalleryField } from '@/components/form/fields/image/GalleryInput';
import { Property, SelectField } from '@/components/form/fields/select/SelectInput';
import { RichTextField } from '@/components/form/fields/text/RichTextInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import { MultiTextAndIconField } from '@/components/form/fields/text/multi/MultiTextAndIconInput';
import { SpecIcons } from '@/components/icons/SpecIcons';
import { useCategories, useManufacturers } from '@/services/api/hooks/catalog';
import { useFormContext } from 'react-hook-form';

import { useRegions } from '@/services/api/hooks/region';
import { useBasicBetterBestOptions } from '@/services/catalogue/useBasicBetterBest';
import { FC, PropsWithChildren, useMemo } from 'react';
export const ProductForm: FC<PropsWithChildren> = ({ children }) => {
  return <div className="mb-8 flex gap-5">{children}</div>;
};

export const ProductDescription = () => {
  return (
    <div className="border-primary-std h-fit min-h-[24rem] w-full rounded-t-lg border bg-white">
      <RichTextField id="description" />
    </div>
  );
};

export const ProductSidebar: FC<PropsWithChildren> = ({ children }) => {
  return <div className="flex w-[32rem] flex-col gap-6">{children}</div>;
};

export const BaseProductProperties = () => {
  const [categories] = useCategories();
  const [manufacturers] = useManufacturers();
  const bbbOptions = useBasicBetterBestOptions();

  const [regions] = useRegions();

  const regionsOptions = useMemo(() => {
    return regions.map(region => {
      return {
        id: region.id,
        name: region.code
      };
    });
  }, [regions]);

  const { setValue, watch } = useFormContext();

  const category = watch('category') as Property;

  const isRegionDisabled = useMemo(() => {
    const region = watch('region');
    return Boolean(region);
  }, []);

  const isMainDevice = useMemo(() => {
    return category && categories.find(item => item.id === category.id)?.isMainDevice;
  }, [category]);

  return (
    <div className="flex flex-col gap-2">
      <label className="text-lg font-medium">Product</label>
      <div className="grid grid-cols-2 gap-2">
        <TextField className="col-span-2" id="name" label="Name" placeholder="Product name" />
        <TextField id="productId" label="Manufacturer SKU" placeholder="SKU001" />
        <TextField id="code" label="SKU" placeholder="SKU001" />
        <SelectField
          id="manufacturer"
          label="Manufacturer"
          placeholder="Manufacturer"
          options={manufacturers.map(manufacturer => ({ id: manufacturer.id, name: manufacturer.name }))}
        />
        <SelectField
          id="category"
          label="Category"
          placeholder="Category"
          options={categories.map(category => ({ id: category.id, name: category.name }))}
        />
        <SelectField
          className="col-span-2"
          id="basicBetterBest"
          label="Basic / Better / Best"
          placeholder="Basic / Better / Best"
          options={bbbOptions}
        />
        <SelectField
          className="col-span-2"
          id="region"
          label="Region"
          placeholder="Region"
          options={regionsOptions}
          disabled={isRegionDisabled}
        />
        <GalleryField
          className="col-span-2"
          id="imageUri"
          label="Image"
          placeholder="Drop or click to upload product image"
        />
        <MultiTextAndIconField
          icons={SpecIcons}
          className="col-span-2"
          id="propertyBag"
          label="Product specifications"
          placeholder="Type to add"
        />
      </div>
    </div>
  );
};
